import { useState } from 'react';
import type { FormEvent } from 'react';
import { pair, ApiError, NetworkError } from '../api';
import { getOrCreateDeviceFingerprint, setDeviceToken } from '../storage';

interface ActivationProps {
  onPaired: (token: string) => void;
}

export function Activation({ onPaired }: ActivationProps) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const fp = getOrCreateDeviceFingerprint();
      const result = await pair(trimmed.toUpperCase(), fp);
      setDeviceToken(result.deviceToken);
      onPaired(result.deviceToken);
    } catch (err) {
      if (err instanceof NetworkError) {
        setError('Cannot reach the server. Check your connection.');
      } else if (err instanceof ApiError) {
        setError(
          err.status === 400 || err.status === 404
            ? 'Invalid or expired activation code.'
            : `Pairing failed (${err.status}).`
        );
      } else {
        setError('Unexpected error. Please try again.');
      }
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && code.trim().length > 0;

  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#0a0a0a',
    }}>
      <div style={{ maxWidth: 480, width: '100%', padding: '2.5rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 300, margin: '0 0 0.5rem' }}>
          Koolyum Signage Player
        </h1>
        <p style={{ fontSize: '1rem', opacity: 0.6, marginBottom: '2.5rem' }}>
          Enter the activation code shown in your dashboard.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX"
            autoFocus
            disabled={submitting}
            spellCheck={false}
            autoComplete="off"
            style={{
              width: '100%', boxSizing: 'border-box',
              fontSize: '2rem', fontFamily: 'ui-monospace, Menlo, monospace',
              letterSpacing: '0.2em', textAlign: 'center', padding: '1rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, color: '#fff', outline: 'none',
              marginBottom: '1.5rem',
            }}
          />
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%', fontSize: '1.1rem', padding: '1rem',
              background: '#fff', color: '#000', border: 'none', borderRadius: 8,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.4, fontWeight: 500,
            }}
          >
            {submitting ? 'Connecting…' : 'Activate'}
          </button>
          {error && (
            <div style={{ marginTop: '1.5rem', color: '#ff6b6b', fontSize: '0.95rem' }}>
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
